import type { AcaoAuditada } from '@arena/shared/dominio';
import type { Recurso } from '@arena/shared/permissoes';
import type {
  CelulaAlterada,
  CodigoDeSenhaGerado,
  MatrizDeAcessos,
  MembroDaEquipe,
  NovoPerfil,
  PaginaDeAuditoria,
  PerfilDesativado,
  PerfilNaMatriz,
  SituacaoDeSeguranca,
} from '@arena/shared/schemas';
import { ErroDeApi, ErroDeRede, api, tokens } from './cliente.js';

/**
 * As chamadas de `#v-acessos`, `#m-perfil` e do console de auditoria.
 *
 * Os tipos vêm de `@arena/shared/schemas` — os MESMOS que o backend usa
 * para responder. `FiltrosDoConsole` é a exceção deliberada: ali os
 * valores são sempre STRING, porque é assim que uma query string existe
 * — quem tipa o formato final (a data, o par "recurso:id" do titular) é
 * o `zConsultaDeAuditoria` do servidor, que já valida os dois lados.
 *
 * Ver: Arena_backend/Docs/plano/19-permissoes-auditoria.md
 */
const BASE = (import.meta.env['VITE_API_URL'] as string | undefined)?.replace(/\/$/, '') ?? '/api';

export const perfisApi = {
  matriz(): Promise<MatrizDeAcessos> {
    return api<MatrizDeAcessos>('/acessos/matriz');
  },

  criar(dados: NovoPerfil): Promise<PerfilNaMatriz> {
    return api<PerfilNaMatriz>('/acessos/perfis', { metodo: 'POST', corpo: dados });
  },

  renomear(id: string, nome: string): Promise<PerfilNaMatriz> {
    return api<PerfilNaMatriz>(`/acessos/perfis/${id}`, { metodo: 'PATCH', corpo: { nome } });
  },

  /** O clique numa célula de `tb-rbac`. */
  alterarCelula(id: string, recurso: Recurso, nivel: string): Promise<CelulaAlterada> {
    return api<CelulaAlterada>(`/acessos/perfis/${id}/permissoes/${recurso}`, {
      metodo: 'PUT',
      corpo: { nivel },
    });
  },

  desativar(id: string): Promise<PerfilDesativado> {
    return api<PerfilDesativado>(`/acessos/perfis/${id}/desativar`, { metodo: 'POST' });
  },

  reativar(id: string): Promise<PerfilNaMatriz> {
    return api<PerfilNaMatriz>(`/acessos/perfis/${id}/reativar`, { metodo: 'POST' });
  },

  excluir(id: string): Promise<void> {
    return api<void>(`/acessos/perfis/${id}`, { metodo: 'DELETE' });
  },

  equipe(): Promise<MembroDaEquipe[]> {
    return api<MembroDaEquipe[]>('/acessos/equipe');
  },

  /**
   * O código de uso único que troca a senha de alguém — "esqueci a senha".
   *
   * O Dono vê o código UMA vez, na resposta, e entrega à pessoa. Não há
   * como lê-lo de novo: o servidor guarda só o hash.
   */
  gerarCodigoDeSenha(membershipId: string): Promise<CodigoDeSenhaGerado> {
    return api<CodigoDeSenhaGerado>(`/acessos/equipe/${membershipId}/codigo-de-senha`, {
      metodo: 'POST',
    });
  },

  /** Mover uma pessoa para outro perfil — o "ou migra os usuários" de #m-perfil. */
  trocarPerfil(membershipId: string, perfilId: string): Promise<MembroDaEquipe> {
    return api<MembroDaEquipe>(`/acessos/equipe/${membershipId}/perfil`, {
      metodo: 'PUT',
      corpo: { perfilId },
    });
  },

  seguranca(): Promise<SituacaoDeSeguranca> {
    return api<SituacaoDeSeguranca>('/acessos/seguranca');
  },
};

/** Os filtros do console, sempre como texto — o formato de uma query string. */
export interface FiltrosDoConsole {
  readonly autor?: string;
  readonly acao?: AcaoAuditada;
  readonly recurso?: string;
  readonly registro?: string;
  /** "recurso:identificador" — já no formato que o servidor espera. */
  readonly titular?: string;
  readonly de?: string;
  readonly ate?: string;
}

function querystring(campos: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(campos)) {
    if (valor === undefined || valor === '') continue;
    params.set(chave, String(valor));
  }
  const texto = params.toString();
  return texto ? `?${texto}` : '';
}

export const auditoriaApi = {
  pagina(filtros: FiltrosDoConsole, cursor?: string, limite = 50): Promise<PaginaDeAuditoria> {
    return api<PaginaDeAuditoria>(`/auditoria${querystring({ ...filtros, cursor, limite })}`);
  },

  /**
   * O CSV, já como arquivo pronto para salvar.
   *
   * Não passa por `api()`: a resposta não é JSON, e o nome do arquivo
   * vem do `content-disposition` que o servidor decide (a data de hoje
   * no fuso da arena).
   */
  async exportar(filtros: FiltrosDoConsole): Promise<{ nomeDoArquivo: string; conteudo: Blob }> {
    const { acesso } = tokens.ler();
    let resposta: Response;
    try {
      resposta = await fetch(`${BASE}/auditoria/exportar${querystring({ ...filtros })}`, {
        headers: acesso ? { authorization: `Bearer ${acesso}` } : {},
      });
    } catch {
      throw new ErroDeRede();
    }

    if (!resposta.ok) {
      const corpo = (await resposta.json().catch(() => ({}))) as {
        erro?: string;
        campos?: Record<string, string[]>;
        requestId?: string;
      };
      throw new ErroDeApi(
        resposta.status,
        corpo.erro ?? 'Não consegui exportar a trilha.',
        corpo.campos,
        corpo.requestId,
      );
    }

    const disposicao = resposta.headers.get('content-disposition') ?? '';
    const nome = /filename="([^"]+)"/.exec(disposicao)?.[1] ?? 'trilha-de-auditoria.csv';
    return { nomeDoArquivo: nome, conteudo: await resposta.blob() };
  },
};

/** Dispara o download do navegador — o "Salvar como" comum de qualquer site. */
export function baixarArquivo(nome: string, conteudo: Blob): void {
  const url = URL.createObjectURL(conteudo);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
